import React, { useEffect, useMemo } from 'react';
import { Form } from '../../../ui/universe';
import { Tooltip } from 'tdesign-react';
import { useCollapsibleList } from '../../../../hooks';

/**
 * 折叠图标组件
 */
const CollapseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.77971 9L7.00005 6.33766L4.22039 9L3.0001 7.83117L7.00005 4L11 7.83117L9.77971 9Z" fill="#32394B" />
  </svg>
);

/**
 * EssayEdu 渲染器 - 作文批改结果
 * @param {Object} props
 * @param {Array} props.essayData - 作文批改数据
 * @param {Function} props.onSelect - 选中回调
 * @param {string} props.selectRect - 当前选中的矩形 key
 */
function EssayEduRenderer({ essayData = [], onSelect, selectRect }) {
  const { isCollapsed, toggleItem } = useCollapsibleList();

  const groupedData = useMemo(() => {
    const dataWithKey = essayData.map((item, index) => ({
      ...item,
      _key: `essay_${index}`,
      _originIndex: index
    }));

    const typeMap = new Map();
    dataWithKey.forEach(item => {
      const type = item.Type || '未知类型';
      if (!typeMap.has(type)) {
        typeMap.set(type, { type, items: [], groupKey: null });
      }
      const group = typeMap.get(type);
      if (group.groupKey === null) {
        group.groupKey = item._key;
      }
      group.items.push(item);
    });

    return Array.from(typeMap.values());
  }, [essayData]);

  useEffect(() => {
    if (essayData.length > 0) {
      onSelect && onSelect('essay_0');
    }
  }, [essayData.length > 0]);

  const isGroupSelected = (group) => group.items.some(item => selectRect === item._key);

  return (
    <div className="edu_container">
      {groupedData.map((group) => {
        const groupCollapsed = isCollapsed(group.groupKey);
        const isSelected = isGroupSelected(group);

        return (
          <div
            key={group.groupKey}
            className={`isSub ${groupCollapsed ? 'isHide' : 'isActive'} ${isSelected ? 'isChecked' : ''} edu_container__item`}
            style={{ marginBottom: '8px' }}
          >
            <div
              className="edu_title_container"
              onClick={() => onSelect && onSelect(group.items[0]._key)}
            >
              <span className="edu_title__text">类型：{group.type}</span>
              <span
                className="edu_title__collaps"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleItem(group.groupKey);
                }}
              >
                <CollapseIcon />
              </span>
            </div>

            <div className="edu_body_container">
              {group.items.map((item) => (
                <div
                  key={item._key}
                  className={`essay_item ${selectRect === item._key ? 'essay_item--selected' : ''}`}
                  onClick={() => onSelect && onSelect(item._key)}
                  style={{ padding: '8px 0', cursor: 'pointer' }}
                >
                  <Form.Item label={item.SubType || '类型'}>
                    <div className="edu_form__item" style={{ color: selectRect === item._key ? '#4a70ff' : 'inherit' }}>
                      {item.Origin || '-'}
                    </div>
                  </Form.Item>

                  {item.Replace && (
                    <Form.Item label=" ">
                      <div className="edu_form__item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Tooltip content={item.Message} theme="light" placement="top">
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                              <path d="M9.23112 14.8503C9.14865 14.8503 9.07704 14.822 9.01628 14.7656C8.95985 14.7135 8.92513 14.6419 8.91211 14.5508C8.81228 13.839 8.70378 13.24 8.58659 12.7539C8.47374 12.2721 8.32183 11.875 8.13086 11.5625C7.93989 11.25 7.68815 11.0004 7.37565 10.8138C7.06749 10.6272 6.67253 10.4774 6.19075 10.3646C5.71332 10.2474 5.12305 10.1432 4.41992 10.0521C4.32444 10.0434 4.24848 10.0087 4.19206 9.94792C4.13563 9.88715 4.10742 9.81337 4.10742 9.72656C4.10742 9.6441 4.13563 9.57248 4.19206 9.51172C4.24848 9.45095 4.32444 9.41623 4.41992 9.40755C5.12305 9.32943 5.71549 9.23611 6.19727 9.1276C6.67904 9.0191 7.07617 8.86936 7.38867 8.67838C7.70117 8.48741 7.95291 8.23351 8.14388 7.91667C8.33485 7.59983 8.48676 7.19835 8.59961 6.71224C8.7168 6.22179 8.82096 5.61849 8.91211 4.90234C8.92513 4.81554 8.95985 4.74609 9.01628 4.69401C9.07704 4.63759 9.14865 4.60937 9.23112 4.60937C9.31792 4.60937 9.38954 4.63759 9.44596 4.69401C9.50239 4.74609 9.53928 4.81554 9.55664 4.90234C9.64779 5.61849 9.74978 6.22179 9.86263 6.71224C9.97982 7.19835 10.1317 7.59983 10.3184 7.91667C10.5093 8.23351 10.7611 8.48741 11.0736 8.67838C11.3861 8.86936 11.7832 9.0191 12.265 9.1276C12.7467 9.23611 13.3414 9.32943 14.0488 9.40755C14.14 9.41623 14.2138 9.45095 14.2702 9.51172C14.3266 9.57248 14.3548 9.6441 14.3548 9.72656C14.3548 9.81337 14.3266 9.88715 14.2702 9.94792C14.2138 10.0087 14.14 10.0434 14.0488 10.0521C13.3414 10.1302 12.7467 10.2235 12.265 10.332C11.7832 10.4405 11.3861 10.5903 11.0736 10.7812C10.7611 10.9679 10.5093 11.2196 10.3184 11.5365C10.1317 11.8533 9.97982 12.2569 9.86263 12.7474C9.74978 13.2378 9.64779 13.839 9.55664 14.5508C9.53928 14.6419 9.50239 14.7135 9.44596 14.7656C9.38954 14.822 9.31792 14.8503 9.23112 14.8503ZM4.81706 7.64974C4.68685 7.64974 4.61306 7.57812 4.5957 7.4349C4.54362 7.00087 4.4872 6.66016 4.42643 6.41276C4.37001 6.16536 4.27669 5.9809 4.14648 5.85937C4.02062 5.73351 3.82964 5.63802 3.57357 5.57292C3.32183 5.50781 2.97461 5.43837 2.5319 5.36458C2.38433 5.34288 2.31055 5.2691 2.31055 5.14323C2.31055 5.0217 2.37565 4.95009 2.50586 4.92839C2.95291 4.84158 3.30447 4.76562 3.56055 4.70052C3.81662 4.63108 4.00977 4.53559 4.13997 4.41406C4.27018 4.29253 4.36567 4.11241 4.42643 3.8737C4.4872 3.63064 4.54362 3.2921 4.5957 2.85807C4.61306 2.71484 4.68685 2.64323 4.81706 2.64323C4.94727 2.64323 5.02105 2.71267 5.03841 2.85156C5.09483 3.28993 5.15126 3.63498 5.20768 3.88672C5.26845 4.13845 5.36176 4.32943 5.48763 4.45964C5.61784 4.5855 5.81098 4.67882 6.06706 4.73958C6.32313 4.80035 6.67687 4.86328 7.12825 4.92839C7.18468 4.93273 7.23025 4.95443 7.26497 4.99349C7.30404 5.03255 7.32357 5.08247 7.32357 5.14323C7.32357 5.26476 7.25846 5.33854 7.12825 5.36458C6.67687 5.45139 6.32313 5.52951 6.06706 5.59896C5.81532 5.66406 5.62435 5.75955 5.49414 5.88542C5.36827 6.00694 5.27496 6.18924 5.21419 6.43229C5.15343 6.67535 5.09483 7.01389 5.03841 7.44792C5.02973 7.50434 5.00586 7.55208 4.9668 7.59115C4.92773 7.63021 4.87782 7.64974 4.81706 7.64974ZM7.9681 3.17057C7.88563 3.17057 7.83789 3.12717 7.82487 3.04036C7.77279 2.77561 7.72721 2.56727 7.68815 2.41536C7.65343 2.26345 7.597 2.14844 7.51888 2.07031C7.4451 1.98785 7.33008 1.92274 7.17383 1.875C7.01758 1.82726 6.79839 1.77951 6.51628 1.73177C6.42947 1.71441 6.38607 1.6645 6.38607 1.58203C6.38607 1.50391 6.42947 1.45616 6.51628 1.4388C6.79839 1.38672 7.01758 1.33898 7.17383 1.29557C7.33008 1.24783 7.4451 1.1849 7.51888 1.10677C7.597 1.02431 7.65343 0.907118 7.68815 0.755208C7.72721 0.603299 7.77279 0.394965 7.82487 0.130208C7.83789 0.0434028 7.88563 0 7.9681 0C8.04622 0 8.09397 0.0434028 8.11133 0.130208C8.15907 0.394965 8.20247 0.603299 8.24154 0.755208C8.2806 0.907118 8.33702 1.02431 8.41081 1.10677C8.48893 1.1849 8.60612 1.24783 8.76237 1.29557C8.91862 1.33898 9.1378 1.38672 9.41992 1.4388C9.50673 1.45616 9.55013 1.50391 9.55013 1.58203C9.55013 1.6645 9.50673 1.71441 9.41992 1.73177C9.1378 1.77951 8.91862 1.82726 8.76237 1.875C8.60612 1.92274 8.48893 1.98785 8.41081 2.07031C8.33702 2.14844 8.2806 2.26345 8.24154 2.41536C8.20247 2.56727 8.15907 2.77561 8.11133 3.04036C8.09397 3.12717 8.04622 3.17057 7.9681 3.17057Z" fill="url(#paint0_linear_essay)"/>
                              <defs>
                                <linearGradient id="paint0_linear_essay" x1="2.31055" y1="0" x2="14.4441" y2="0.0733447" gradientUnits="userSpaceOnUse">
                                  <stop offset="0.15" stopColor="#6247F1"/>
                                  <stop offset="1" stopColor="#D775FF"/>
                                </linearGradient>
                              </defs>
                            </svg>
                            <span>{item.Replace}</span>
                          </span>
                        </Tooltip>
                      </div>
                    </Form.Item>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default EssayEduRenderer;
